import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ID = "site-feedback-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dbb1fnq9ke8mc8ad7cf0";

const principles = [
  {
    icon: "ri-file-list-3-line",
    title: "剧情优先",
    desc: "所有章节按叙事节点整理，先讲清楚发生了什么，再讨论它为什么重要。",
  },
  {
    icon: "ri-search-eye-line",
    title: "便于检索",
    desc: "支持关键词、时间线与时长排序，想找某场戏不必从头翻到尾。",
  },
  {
    icon: "ri-shield-check-line",
    title: "边界清晰",
    desc: "播放区域明确标注为演示模块，不提供片源，不误导观看预期。",
  },
];

const steps = [
  { step: "01", title: "选择章节", desc: "在分类页按时间线或关键词找到想看的段落。" },
  { step: "02", title: "阅读剧情", desc: "详情页提供分段解说、影片资料与相关推荐。" },
  { step: "03", title: "收藏与回看", desc: "点收藏即可保存在本机，下次刷新仍能找回。" },
  { step: "04", title: "查看说明", desc: "播放区域为演示模块，正式接口尚未接入。" },
];

export default function About() {
  usePageMeta({
    title: "关于色戒无删减版 - 站点简介与联系反馈",
    description:
      "色戒无删减版影视平台，旨在为用户提供完整版电影资源和详尽剧情解析。本页包含站点定位、使用说明、免责声明、隐私说明与反馈表单。",
    keywords: "色戒无删减版介绍,影视平台简介,色戒剧情解析,联系反馈,免责声明",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = serverMessage.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMessage || "提交失败，请稍后重试，或通过邮箱与我们联系。");
        return;
      }

      setStatus("success");
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setFormError("网络连接异常，提交未成功。请检查网络后再试一次。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden border-b border-background-200 pb-12 pt-24 md:pb-16 md:pt-36">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=quiet%20dark%20cinema%20lobby%20with%20vintage%20poster%20frames%2C%20brass%20wall%20sconces%20casting%20warm%20amber%20light%2C%20deep%20burgundy%20velvet%20bench%2C%20atmospheric%20haze%2C%20cinematic%20wide%20composition%2C%20no%20text&width=1920&height=700&seq=sj-about-hero&orientation=landscape"
              alt="关于页背景"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-50/90" />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/40" />
          </div>

          <div className="container-page relative">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于" }]} />
            <h1 className="mt-5 max-w-3xl font-heading text-3xl leading-tight text-foreground-950 md:text-[42px]">
              关于<span className="text-accent-500">色戒无删减版</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
              色戒无删减版影视平台，旨在为用户提供完整版电影资源和详尽剧情解析。我们把影片按剧情拆成章节，配上原创解说与幕后资料，让想认真看故事的人有一个清晰的入口。
            </p>
          </div>
        </section>

        <section className="container-page py-12 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20vintage%20reading%20corner%20with%20a%20wooden%20desk%2C%20stacked%20film%20reels%20and%20old%20books%2C%20amber%20desk%20lamp%20light%2C%20deep%20wine%20red%20wall%2C%20soft%20haze%2C%20quiet%20cinematic%20interior%2C%20no%20text&width=1000&height=750&seq=sj-about-room&orientation=landscape"
                alt="站点编辑工作区氛围图"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-md border border-foreground-950/15 bg-background-50/70 px-4 py-3 backdrop-blur">
                <p className="font-label text-[11px] tracking-[0.18em] text-accent-500">站点定位</p>
                <p className="mt-1 text-sm text-foreground-900">影视资讯 · 剧情解析 · 观影指引</p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-8 bg-accent-500" />
                <span className="font-label text-xs tracking-[0.28em] text-accent-500">我们做什么</span>
              </div>
              <h2 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-[32px]">
                不急着下结论，
                <br className="hidden sm:block" />
                先把故事讲清楚
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                这是一个以剧情档案为核心的影视内容站。我们整理章节脉络、人物关系与镜头细节，也把相关的同类影片与深度文章放在一起，方便你顺着兴趣继续往下看。
              </p>
              <div className="mt-6 flex flex-col gap-3">
                {principles.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 rounded-md border border-background-200 bg-background-100 p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-accent-500/30 bg-accent-500/10 text-accent-400">
                      <i className={`${item.icon} text-base leading-none`} />
                    </span>
                    <div>
                      <h3 className="font-heading text-sm text-foreground-950">{item.title}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100/40 py-12 md:py-16">
          <div className="container-page">
            <div className="flex items-center gap-2">
              <span className="h-px w-8 bg-accent-500" />
              <span className="font-label text-xs tracking-[0.28em] text-accent-500">使用说明</span>
            </div>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-[30px]">四步找到你想看的内容</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((item) => (
                <div key={item.step} className="surface-card p-5">
                  <span className="font-heading text-2xl text-accent-500/50">{item.step}</span>
                  <h3 className="mt-2 font-heading text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="container-page py-12 md:py-16">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div id="disclaimer" className="surface-card scroll-mt-24 p-6 md:p-8">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-base leading-none text-accent-500" />
                <h2 className="font-heading text-lg text-foreground-950">免责声明</h2>
              </div>
              <div className="mt-4 space-y-3 text-xs leading-relaxed text-foreground-600 md:text-sm">
                <p>
                  本站为影视内容展示与剧情解析类演示站点，不提供、不存储、不传播任何影片文件或播放源，也不提供下载链接。
                </p>
                <p>
                  页面中的播放器区域为前端演示模块，仅用于展示界面结构，不含真实播放功能。正式播放接口尚未接入。
                </p>
                <p>
                  站内剧情解说、影评与专题文章均为原创整理内容。影片基本信息仅作示例展示，部分演职员信息为演示用虚构内容，请以官方发布为准。
                </p>
                <p>
                  如本站内容涉及版权问题，请通过下方反馈表单与我们联系，我们会在核实后及时处理。
                </p>
              </div>
            </div>

            <div id="privacy" className="surface-card scroll-mt-24 p-6 md:p-8">
              <div className="flex items-center gap-2">
                <i className="ri-lock-2-line text-base leading-none text-accent-500" />
                <h2 className="font-heading text-lg text-foreground-950">用户隐私声明</h2>
              </div>
              <div className="mt-4 space-y-3 text-xs leading-relaxed text-foreground-600 md:text-sm">
                <p>
                  本站不需要注册或登录。章节收藏状态仅保存在你当前浏览器的本地存储中，不会上传到服务器，也不会同步到其他设备。
                </p>
                <p>
                  清除浏览器缓存或更换设备后，收藏记录会一并清空，这属于正常现象。
                </p>
                <p>
                  当你提交反馈表单时，我们仅收集你主动填写的称呼、邮箱、反馈类型与内容，用于回复与改进站点，不会用于其他用途。
                </p>
                <p>
                  本站不使用第三方广告追踪脚本；页面统计仅依赖平台自带的基础访问数据。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="container-page scroll-mt-24 pb-16 md:pb-24">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
            <div className="surface-card p-6 md:p-8">
              <div className="flex items-center gap-2">
                <span className="h-px w-8 bg-accent-500" />
                <span className="font-label text-xs tracking-[0.28em] text-accent-500">联系与反馈</span>
              </div>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950">有话想对我们说</h2>
              <p className="mt-2 text-sm text-foreground-600">
                内容纠错、章节建议、版权问题或其他想法，都可以在这里提交。我们通常会在 2 个工作日内查看。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <label htmlFor="contact-name" className="font-label text-xs text-foreground-600">
                      你的称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="例如：阿明"
                      className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <label htmlFor="contact-email" className="font-label text-xs text-foreground-600">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于接收回复"
                      className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <label htmlFor="contact-topic" className="font-label text-xs text-foreground-600">
                    反馈类型
                  </label>
                  <div className="relative">
                    <select
                      id="contact-topic"
                      name="topic"
                      required
                      defaultValue=""
                      className="box-border w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-900 focus:border-accent-500 focus:outline-none"
                    >
                      <option value="" disabled>
                        请选择反馈类型
                      </option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="章节建议">章节建议</option>
                      <option value="版权问题">版权问题</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base leading-none text-foreground-500" />
                  </div>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="contact-message" className="font-label text-xs text-foreground-600">
                      反馈内容
                    </label>
                    <span className="font-label text-[11px] text-foreground-500">{messageLength} / 500</span>
                  </div>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请简单描述你的问题或建议，最多 500 字"
                    className="box-border w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="field-honey"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-base leading-none`} />
                    {status === "submitting" ? "正在提交" : "提交反馈"}
                  </button>
                  <p className="text-[11px] leading-relaxed text-foreground-500">
                    提交即表示你同意我们使用该信息回复本次反馈。
                  </p>
                </div>

                {status === "success" && (
                  <div className="flex items-start gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 p-3">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none text-accent-400" />
                    <p className="text-xs leading-relaxed text-foreground-800">
                      反馈已提交，感谢你的耐心。我们会尽快查看并回复到你留下的邮箱。
                    </p>
                  </div>
                )}

                {status === "error" && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-700/50 bg-primary-500/10 p-3">
                    <i className="ri-error-warning-line mt-0.5 text-base leading-none text-primary-800" />
                    <p className="text-xs leading-relaxed text-foreground-800">
                      {formError || "提交失败，请稍后重试。"}
                    </p>
                  </div>
                )}
              </form>
            </div>

            <div className="flex flex-col gap-5">
              <div className="surface-card p-6">
                <h2 className="font-heading text-base text-foreground-950">其他联系方式</h2>
                <p className="mt-1.5 text-xs text-foreground-500">工作时间：周一至周五 10:00 - 18:00</p>
                <div className="mt-5 flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-200/50 text-accent-400">
                      <i className="ri-mail-line text-base leading-none" />
                    </span>
                    <div>
                      <p className="font-label text-[11px] tracking-[0.16em] text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-900">contact@sejie-archive.example</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-200/50 text-accent-400">
                      <i className="ri-map-pin-line text-base leading-none" />
                    </span>
                    <div>
                      <p className="font-label text-[11px] tracking-[0.16em] text-foreground-500">内容编辑</p>
                      <p className="text-sm text-foreground-900">线上协作 · 不支持线下到访</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-200/50 text-accent-400">
                      <i className="ri-time-line text-base leading-none" />
                    </span>
                    <div>
                      <p className="font-label text-[11px] tracking-[0.16em] text-foreground-500">回复时效</p>
                      <p className="text-sm text-foreground-900">2 个工作日内查看</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <p className="font-label text-[11px] tracking-[0.18em] text-accent-500">继续浏览</p>
                <h3 className="mt-2 font-heading text-base text-foreground-950">还没找到想看的章节？</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  试试按关键词检索，或者从时间线的第一个阶段开始。
                </p>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <Link
                    to="/category"
                    className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-xs text-foreground-950 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-list-check text-sm leading-none" />
                    章节分类
                  </Link>
                  <Link
                    to="/special"
                    className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 font-label text-xs text-foreground-800 transition-colors hover:border-accent-500"
                  >
                    <i className="ri-book-open-line text-sm leading-none" />
                    专题合辑
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}